<template>
	<PageLayoutSidebar orientation="right">
		<div class="flex flex-col items-start gap-10 pb-6">
			<ApiErrorCard v-if="apiError" :error="apiError" />
			<LoanApplicationEditorForm
				v-else-if="isAsyncDataReady"
				ref="editor-form-body"
				v-model="formData"
				:class="selectedFilter === 'relevant' ? 'show-relevant-fields' : ''"
				:schema
				:validate
			/>
			<LoanApplicationSubmitModal
				v-model="showSubmitModal"
				:loan-application-id
				@success="onSubmitSuccess"
			/>
		</div>

		<template #sidebar>
			<LoanApplicationEditorSidebar
				v-if="application && formData && schema && formKitNode"
				v-model="selectedFilter"
				:version-number="application.hdnDossier.requestVersion"
				:is-revision="application.hdnDossier.requestVersion > 1"
				:has-unsaved-changes
				:form-kit-node
				:links
				@upload="upload"
				@submit="onSubmitClick"
				@download="onDownload"
			/>
		</template>
	</PageLayoutSidebar>
</template>

<script setup lang="ts">
import type {
	FormKitGroupValue,
	FormKitNode,
	FormKitSchemaNode,
} from "@formkit/core";
import { saveAs } from "file-saver";
import { computed, ref, toRef, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import { useEditorSidebarLinks } from "@/composables/useEditorNavigationLinks";
import { useLoanApplicationFormOverrider } from "@/composables/useLoanApplicationFormOverrides";
import { useXsdToFormKit } from "@/composables/useXsdToFormKit";
import {
	ContentType,
	type HdnLoanApplicationSchema,
	type LoanApplicationDetails,
} from "@/generatedApi";
import { api } from "@/loanLinkApi";
import type { ApiError } from "@/models/ApiError";

interface Props {
	loanApplicationId: string;
}
const props = defineProps<Props>();

const router = useRouter();

// async values loaded from api
const schema = ref<FormKitSchemaNode[] | null>(null);
const application = ref<LoanApplicationDetails | null>(null);
const formData = ref<FormKitGroupValue | undefined>();
const apiError = ref<ApiError | null>(null);
const isAsyncDataReady = computed(
	() =>
		schema.value !== null &&
		formData.value !== null &&
		application.value !== null,
);
const selectedFilter = ref<undefined | "relevant">(undefined);
watch(selectedFilter, () => {
	myForm.value?.formKitNode?.context?.fns.manualValidate?.();
});

const initForm = async () => {
	const getFormSchemaFromSchema = (schema: HdnLoanApplicationSchema) => {
		const xsdJsonRef = toRef(schema.xsdJson);
		const { overrider } = useLoanApplicationFormOverrider();
		const { schema: formKitSchema } = useXsdToFormKit(xsdJsonRef, overrider);
		return formKitSchema.value;
	};

	await Promise.all([
		api.loanApplication
			.getApplication(props.loanApplicationId)
			.then((res) => (application.value = res.data))
			.catch((res) => (apiError.value = res.error)),

		api.loanApplication
			.getApplicationContentSchema(props.loanApplicationId)
			.then((res) => {
				const xsdSchema = res.data;
				schema.value = getFormSchemaFromSchema(xsdSchema);
			})
			.catch((res) => (apiError.value = res.error)),

		api.loanApplication
			.getApplicationContents(props.loanApplicationId)
			.then((res) => (formData.value = res.data))
			.catch((res) => (apiError.value = res.error)),
	]);
};

await initForm();

const hasUnsavedChanges = ref(false);
watch(formData, () => (hasUnsavedChanges.value = true), {
	deep: true,
});

// note: without typing the template ref CI fails
const myForm = useTemplateRef<{ formKitNode: FormKitNode }>("editor-form-body");
const formKitNode = computed(() => {
	return myForm.value?.formKitNode ? myForm.value.formKitNode : null;
});

// for sidebar
const { links } = useEditorSidebarLinks(formKitNode);

const showSubmitModal = ref(false);

const save = async (formData: { OfferteAanvraag: Record<string, any> }) => {
	await api.loanApplication
		.updateApplicationContents(props.loanApplicationId, formData)
		.then(() => (hasUnsavedChanges.value = false))
		.catch((res) => (apiError.value = res.error));
};

const validate = async (formData: { OfferteAanvraag: Record<string, any> }) => {
	await save(formData);
	const validationErrors =
		await api.loanApplication.validateApplicationContents(
			props.loanApplicationId,
		);
	return validationErrors.data;
};

async function upload(file: File) {
	const xmlFile = await file.text();
	await api.loanApplication
		// @ts-expect-error - xmlFile is a string, but updateApplicationContents expects an object
		.updateApplicationContents(props.loanApplicationId, xmlFile, {
			type: ContentType.Text,
		});
}

async function onSubmitSuccess() {
	await router.push({
		name: "LoanApplicationDetailsPage",
	});
}

function onSubmitClick() {
	showSubmitModal.value = true;
}

async function onDownload() {
	const { data: xml } = await api.loanApplication.getApplicationXml(
		props.loanApplicationId,
		{ format: "blob" },
	);

	saveAs(xml, `${props.loanApplicationId}.xml`);
}
</script>

<style lang="scss">
@use "@/style/mixins.scss" as mixins;

.hdn-editor.show-relevant-fields
	.formkit-outer[data-filled-or-invalid="false"] {
	display: none;
}

.hdn-editor {
	display: flex;
	gap: 0.75rem;
	font-size: 0.8125rem;
	width: 100%;

	h1.hdn-form-heading,
	h2.hdn-form-heading,
	h3.hdn-form-heading {
		font-size: 1.25rem;
	}

	h4.hdn-form-heading,
	h5.hdn-form-heading,
	h6.hdn-form-heading {
		font-size: 1rem;
	}

	.filter-excluded-input {
		display: none !important;
	}

	.loan-application-schemas > * {
		[data-type="repeater"]:not([data-empty="true"]),
		[data-type="group"] {
			&:not(:first-child) {
				margin-top: 2rem;
			}

			&:not(:last-child) {
				margin-bottom: 2rem;
			}
		}
	}

	.header-button {
		font-size: 1em;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.25rem;
		border-radius: var(--border-radius);
		border: 1px solid var(--border);
		background: #fff;
		color: var(--text-secondary);
		cursor: pointer;

		&:hover:not(:disabled) {
			background: var(--background-secondary);
		}

		&:disabled {
			opacity: 0.6;
			cursor: default;
		}

		.btn-icon {
			font-size: 1.25rem;
		}
	}

	* {
		scroll-margin-top: 1rem;
	}

	.formkit-messages {
		list-style: inside;
	}

	.app-card {
		@include mixins.app-card;
	}

	.loan-application-schemas {
		display: flex;
		flex-direction: column;
		gap: 3rem;
		flex: 0 1 100%;
		max-width: 750px;
		margin: 0 auto;

		> div > fieldset > div > ul {
			gap: 1rem;
			flex-direction: column;
			display: flex;
		}
	}

	.row {
		display: flex;
		flex-direction: row;
		gap: 0.25rem;
	}

	.header-row {
		display: flex;
		gap: 0.5rem;
		align-items: center;

		.header-button {
			align-self: unset;
			padding: 0.25rem;
		}
	}

	fieldset {
		padding: 0;
		border: none;
		display: flex;
		flex-direction: column;
		gap: 1rem;

		&.formkit-fieldset {
			gap: 0;
		}
	}

	> fieldset {
		gap: 4rem;
		flex-basis: 600px;
	}

	.dev-modeldata {
		width: 300px;
	}

	ul {
		list-style: none;
		padding: 0;
	}

	.formkit-items {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.formkit-summary-header {
		font-size: 1rem;
	}

	.formkit-option {
		&:hover {
			input[type="radio"]:not(:checked) {
				border: 1px solid #a99cb6;
			}
		}

		.formkit-wrapper {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 0.375rem;

			.formkit-inner {
				display: flex;
				align-items: center;
				justify-content: center;
			}
		}

		input[type="radio"] {
			border-radius: 50%;
			border: 1px solid #cbc1d2;
			width: 1rem;
			height: 1rem;
			padding: 0;

			&:focus-visible {
				outline: 2px solid var(--accent-primary);
				outline-offset: 1px;
			}

			&:checked {
				border: 5px solid var(--accent-primary);
			}
		}
	}

	.formkit-wrapper {
		display: flex;
		flex-direction: column;
		gap: 0.125rem;
		font-size: 0.8125rem;
		align-items: flex-start;

		input {
			appearance: none;
			width: 100%;
		}
	}

	.formkit-outer {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
		position: relative;

		&[data-invalid="true"] {
			> .formkit-fieldset {
				> .formkit-legend,
				> .formkit-inner > .header-row {
					color: var(--color-warn);
				}
			}

			> .formkit-wrapper {
				> .formkit-label {
					color: var(--color-warn);
				}

				> .formkit-inner {
					border-color: var(--color-warn);
				}
			}
		}

		&[data-type="radio"] {
			.formkit-help {
				order: 99;
			}
		}
	}

	.formkit-label,
	.formkit-legend {
		font-weight: 600;
		font-size: 0.8125rem;
		padding: 0;
		display: flex;
		flex-direction: row;
		gap: 0.25rem;
		cursor: default;

		.hdn-input-label-secondary {
			font-weight: 400;
			color: var(--text-muted);
		}
	}

	.formkit-asterisk {
		color: var(--accent-primary);
	}

	.formkit-messages,
	.formkit-messages a {
		color: var(--color-warn);
	}

	.formkit-messages a {
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	.formkit-help {
		max-width: 300px;
	}

	.formkit-options,
	.formkit-options-wrapper {
		display: flex;
		align-items: center;
	}

	.formkit-options {
		min-height: 1.5rem;
		gap: 0.75rem;
	}

	.formkit-options-wrapper {
		gap: 0.5rem;

		.formkit-remove-control {
			padding: 0;
			border: none;
			background: none;
			color: var(--text-muted);

			&:not(:disabled):hover {
				color: var(--text-primary);
			}
		}
	}

	.formkit-inner {
		> .formkit-input,
		> div > .formkit-input {
			border-radius: 4px;
			border: 1px solid var(--border);
			background: #fff;

			&:focus {
				@include mixins.native-focus;
			}
		}
	}

	.formkit-input {
		padding: 0.25rem 0.4rem;
		outline: none;
		width: 100%;
		background: none;
	}

	.formkit-option {
		.formkit-inner {
			border: none;

			&:has(> .formkit-input:focus) {
				outline: none;
			}
		}
	}
}

.hdn-editor-quicknav {
	scrollbar-gutter: stable;
	scrollbar-width: thin;
	scrollbar-color: var(--scrollbar-color) transparent;

	font-size: 0.8125rem;

	h1,
	h2,
	h3 {
		font-size: 1.25rem;
	}

	h4,
	h5,
	h6 {
		font-size: 1rem;
	}
}

.v-popper--theme-tooltip {
	font-size: 0.8125rem;
	max-width: 300px;

	.v-popper__inner {
		padding: 0.25rem 0.5rem;
		border-radius: 0.25rem;
	}
}
</style>
